/**
 * Investor-Portal Dashboard (#1196 v2.577.0) — Cockpit-Rebuild.
 *
 * Layout:
 *   1. Header + PeriodToggle + Sync
 *   2. DataMaturityBanner (#1184)
 *   3. KPI-Strip (4 Werte: Umsatz / Rohertrag / EBITDA / EBITDA-Marge) + Liquiditäts-Card
 *   4. 12-Monats Soll/Ist (Plan vs IST, vorläufige Monate gelb)
 *   5. Segment-DB-Mini-Übersicht (5 Segmente klickbar)
 *   6. Cockpit-Quicklinks (Cashflow / Szenario / Audit / Report) mit Live-Status
 *   7. Top-Abweichungen €-sortiert (% nur wenn plausibel)
 *
 * Touch-frei: Segment-/Cashflow-/Szenario-/Audit-/Report-Pages bleiben unverändert.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { Wallet, Activity, ShieldCheck, FileText, ArrowRight, Info } from 'lucide-react';
import PeriodToggle, { buildPeriodQuery, isCustomPeriod } from './components/PeriodToggle';
import DataMaturityBanner from './components/DataMaturityBanner';
import { MaturityOverlay, reifeFooter } from './components/MaturityChartOverlay';
import { defaultMatureMonthState, applyYtdCutoff, MONTH_LABELS_DE } from '../../utils/data-maturity';

const API = import.meta.env.VITE_API_URL || '';

const MONTH_LABELS_SHORT = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'];

function fmtEur(v, opts = {}) {
  if (v === null || v === undefined || Number.isNaN(v)) return '–';
  return new Intl.NumberFormat('de-AT', {
    style: 'currency', currency: 'EUR',
    maximumFractionDigits: opts.fractionDigits ?? 0,
  }).format(v);
}
function fmtPct(v, withSign = true) {
  if (v === null || v === undefined || Number.isNaN(v)) return '–';
  const sign = withSign && v > 0 ? '+' : '';
  return `${sign}${v.toFixed(1)} %`;
}
function fmtPctPoints(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return '–';
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(2)} pp`;
}
function deltaColor(v) {
  if (v === null || v === undefined) return 'text-[#6B7280]';
  if (v > 0) return 'text-emerald-600';
  if (v < 0) return 'text-red-600';
  return 'text-[#6B7280]';
}
function labelize(p) {
  return p.replace(/^guv_|^bilanz_|^cashflow_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const SEGMENT_HREF = {
  outbound: '/investor/segmente/outbound',
  inbound: '/investor/segmente/inbound',
  lager: '/investor/segmente/lager',
  carrier: '/investor/segmente/carrier',
  material: '/investor/segmente/material',
};

const CHART_POSITIONS = [
  { key: 'guv_gesamtumsatz_de_at', label: 'Gesamtumsatz' },
  { key: 'guv_rohertrag', label: 'Rohertrag' },
  { key: 'guv_ebitda', label: 'EBITDA' },
  { key: 'guv_personalkosten_lager', label: 'Personalkosten Lager' },
];

const AMPEL_STYLE = {
  green: { bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500', text: 'text-emerald-700', label: 'OK' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-200', dot: 'bg-amber-500', text: 'text-amber-700', label: 'Achtung' },
  red:   { bg: 'bg-red-50',   border: 'border-red-200',   dot: 'bg-red-500',     text: 'text-red-700',     label: 'Kritisch' },
  unknown:{bg: 'bg-[#F8F9FC]',border: 'border-[#E5E7EB]', dot: 'bg-[#9CA3AF]',   text: 'text-[#6B7280]',   label: '–' },
};

function AmpelDot({ level }) {
  const s = AMPEL_STYLE[level] || AMPEL_STYLE.unknown;
  return <span className={`inline-block w-2 h-2 rounded-full ${s.dot}`} />;
}

export default function InvestorDashboard() {
  const [periodState, setPeriodState] = useState(() => {
    const base = defaultMatureMonthState();
    return { period: 'ytd', year: base.year, month: base.month,
      quarter: Math.ceil(base.month / 3), fromDate: null, toDate: null };
  });

  // Frontend-only YTD-Cutoff (Datenreife #1184) — Backend sieht den verkürzten Range
  const effectivePeriodState = useMemo(() => applyYtdCutoff(periodState), [periodState]);
  const periodQuery = useMemo(() => buildPeriodQuery(effectivePeriodState), [effectivePeriodState]);

  const [kpis, setKpis] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [chartPos, setChartPos] = useState(CHART_POSITIONS[0].key);
  const [chartData, setChartData] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);
  const [filterConfig, setFilterConfig] = useState(null);
  const [filterBusy, setFilterBusy] = useState(false);

  const [segmentMini, setSegmentMini] = useState(null);
  const [liquidity, setLiquidity] = useState(null);
  const [auditMini, setAuditMini] = useState(null);
  const [reportMini, setReportMini] = useState(null);

  async function loadKpis() {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/investor/dashboard/kpis?${periodQuery}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setKpis(data.kpis || []);
        setLastSync(data.last_synced || null);
      }
    } catch (e) {
      console.error('[Investor] kpi load fail', e);
    } finally {
      setLoading(false);
    }
  }

  async function loadChart() {
    try {
      const res = await fetch(`${API}/api/investor/positions-chart/${encodeURIComponent(chartPos)}?year=${periodState.year}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setChartData((data.data || []).map(d => ({
          monat: MONTH_LABELS_SHORT[d.monat - 1],
          monatIdx: d.monat,
          Plan: d.plan,
          IST: d.ist || null,
        })));
      }
    } catch (e) {
      console.error('[Investor] chart load fail', e);
    }
  }

  async function loadAlerts() {
    try {
      // €-sortiert (#1196): default min_delta_abs=500, min_plan_for_pct=1000
      const res = await fetch(`${API}/api/investor/alerts?${periodQuery}&limit=8`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAlerts(data.alerts || []);
      }
    } catch (e) {
      console.error('[Investor] alerts load fail', e);
    }
  }

  async function loadFilterConfig() {
    try {
      const res = await fetch(`${API}/api/investor/sync/config`, { credentials: 'include' });
      if (res.ok) setFilterConfig(await res.json());
    } catch (e) {
      console.error('[Investor] filter-config load fail', e);
    }
  }

  async function loadDashboardMini() {
    const fetchSafe = async (url, setter) => {
      try {
        const res = await fetch(`${API}${url}`, { credentials: 'include' });
        if (res.ok) setter(await res.json());
        else setter(null);
      } catch { setter(null); }
    };
    await Promise.all([
      fetchSafe(`/api/investor/dashboard/segment-mini?${periodQuery}`, setSegmentMini),
      fetchSafe(`/api/investor/dashboard/liquidity-mini?${periodQuery}`, setLiquidity),
      fetchSafe(`/api/investor/dashboard/audit-mini?${periodQuery}`, setAuditMini),
      fetchSafe(`/api/investor/dashboard/report-mini`, setReportMini),
    ]);
  }

  async function toggleOnlyPosted(value) {
    if (!filterConfig) return;
    setFilterBusy(true);
    setSyncMsg('Filter wird umgestellt + Cache neu berechnet (5–10s)…');
    try {
      const res = await fetch(`${API}/api/investor/sync/config`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ onlyPosted: value }),
      });
      if (res.ok) {
        const body = await res.json();
        setFilterConfig({ onlyPosted: body.onlyPosted });
        const resync = body.resync;
        if (resync && resync.ok) {
          setSyncMsg(`${value ? 'Filter aktiv: nur gebuchte Posten.' : 'Filter inaktiv: Entwürfe inkludiert.'} (${resync.months} Monate neu berechnet)`);
        } else if (resync && !resync.ok) {
          setSyncMsg(`Filter gesetzt, Cache-Resync fehlgeschlagen: ${resync.error}`);
        } else {
          setSyncMsg(value ? 'Filter aktiv: nur gebuchte Posten.' : 'Filter inaktiv: Entwürfe inkludiert.');
        }
        await Promise.all([loadKpis(), loadAlerts(), loadChart(), loadDashboardMini()]);
      } else if (res.status === 403) {
        setSyncMsg('Filter-Änderung erfordert Admin-Rolle.');
      }
    } catch (e) {
      setSyncMsg('Filter-Update fehlgeschlagen: ' + e.message);
    } finally {
      setFilterBusy(false);
    }
  }

  useEffect(() => { loadKpis(); loadAlerts(); loadDashboardMini(); }, [periodQuery]); // eslint-disable-line
  useEffect(() => { loadChart(); }, [periodState.year, chartPos]); // eslint-disable-line
  useEffect(() => { loadFilterConfig(); }, []); // eslint-disable-line

  async function triggerSync() {
    setSyncBusy(true);
    setSyncMsg(null);
    try {
      const res = await fetch(`${API}/api/investor/sync/odoo`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (res.ok) {
        setSyncMsg(`Sync ok: ${data.positions_written} Positionen aus ${data.months} Monaten (${(data.elapsed_ms / 1000).toFixed(1)}s).`);
        await Promise.all([loadKpis(), loadChart(), loadAlerts(), loadDashboardMini()]);
      } else if (res.status === 429) {
        setSyncMsg(`Cooldown: nächster Sync in ${data.retry_after_seconds}s.`);
      } else {
        setSyncMsg(`Fehler: ${data.error || data.message}`);
      }
    } catch (e) {
      setSyncMsg('Sync fehlgeschlagen: ' + e.message);
    } finally {
      setSyncBusy(false);
    }
  }

  const lastSyncLabel = useMemo(() => {
    if (!lastSync) return 'Noch kein IST-Sync';
    const d = new Date(lastSync);
    return `Letzter Sync: ${d.toLocaleString('de-AT', { dateStyle: 'short', timeStyle: 'short' })}`;
  }, [lastSync]);

  return (
    <div className="space-y-6">
      {/* ─── Header ────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-lg font-semibold text-[#1A1A2E]">Dashboard</h1>
          <div className="text-xs text-[#6B7280] mt-0.5">{lastSyncLabel}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PeriodToggle
            period={periodState.period}
            year={periodState.year}
            month={periodState.month}
            quarter={periodState.quarter}
            fromDate={periodState.fromDate}
            toDate={periodState.toDate}
            onChange={setPeriodState}
          />
          <button
            onClick={triggerSync}
            disabled={syncBusy}
            className="text-xs px-3 py-1.5 rounded-md bg-[#338edc] text-white font-medium hover:bg-[#2a7bc4] disabled:opacity-60"
          >
            {syncBusy ? 'Sync läuft…' : 'Odoo-Sync'}
          </button>
        </div>
      </div>

      <DataMaturityBanner periodState={periodState} />

      {isCustomPeriod(periodState) && (
        <div className="text-xs text-[#1A1A2E] bg-[#FFF8E1] border border-[#FFE082] rounded-lg px-3 py-2 flex items-start gap-2">
          <span className="text-[#F59E0B] mt-px">●</span>
          <div>
            <strong>Tagesgenau-Modus:</strong> IST-Werte werden live aus Odoo geladen (
            {periodState.fromDate} – {periodState.toDate}). Plan-Werte sind anteilig hochgerechnet.
          </div>
        </div>
      )}

      {syncMsg && (
        <div className="text-xs text-[#4B5563] bg-white border border-[#E5E7EB] rounded-lg px-3 py-2">
          {syncMsg}
        </div>
      )}

      {filterConfig && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h2 className="text-sm font-semibold text-[#1A1A2E]">Datenfilter (Odoo-UGB-Bericht)</h2>
              <p className="text-[11px] text-[#6B7280] mt-0.5">
                IST-Werte stammen aus „Gewinn- und Verlustrechnung nach § 231 UGB (AT)". Änderung → „Odoo-Sync" klicken.
              </p>
            </div>
          </div>
          <div className="mt-3">
            <label
              className={`flex items-start gap-2 p-2 rounded-md border ${filterConfig.onlyPosted ? 'border-[#338edc] bg-[#338edc]/5' : 'border-[#E5E7EB] bg-white'} cursor-pointer max-w-md`}
              title="Wenn aktiv: nur freigegebene Buchungen (parent_state=posted). Wenn inaktiv: zusätzlich Entwürfe (draft). Default: aktiv."
            >
              <input
                type="checkbox"
                disabled={filterBusy}
                checked={!!filterConfig.onlyPosted}
                onChange={e => toggleOnlyPosted(e.target.checked)}
                className="mt-0.5"
              />
              <div className="text-xs">
                <div className="font-medium text-[#1A1A2E]">Nur gebuchte Posten</div>
                <div className="text-[#6B7280] mt-0.5">
                  Aktiv: nur freigegebene Buchungen (parent_state=posted). Inaktiv: zusätzlich Entwürfe. Default: aktiv.
                </div>
              </div>
            </label>
          </div>
        </div>
      )}

      {/* ─── KPI-Strip (4 KPIs) + Liquiditäts-Card ────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
        {(kpis || []).map(k => (
          <KpiCard key={k.key} kpi={k} />
        ))}
        {loading && !kpis && Array(4).fill(0).map((_, i) => (
          <div key={i} className="bg-white border border-[#E5E7EB] rounded-xl p-4 h-[100px] animate-pulse" />
        ))}
        <LiquidityCard data={liquidity} />
      </div>

      {/* ─── 12-Monats Soll/Ist mit Datenreife-Overlay ──────── */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="text-sm font-semibold text-[#1A1A2E]">12-Monats Soll / Ist {periodState.year}</h2>
          <select
            value={chartPos}
            onChange={e => setChartPos(e.target.value)}
            className="text-xs border border-[#E5E7EB] rounded-md px-2 py-1.5 bg-white"
          >
            {CHART_POSITIONS.map(p => (
              <option key={p.key} value={p.key}>{p.label}</option>
            ))}
          </select>
        </div>
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="monat" stroke="#6B7280" fontSize={11} />
              <YAxis stroke="#6B7280" fontSize={11} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                formatter={(v) => fmtEur(v)}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E5E7EB' }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {MaturityOverlay({ year: periodState.year, monthLabels: MONTH_LABELS_SHORT })}
              <Line type="monotone" dataKey="Plan" stroke="#9CA3AF" strokeWidth={2} strokeDasharray="4 4" dot={false} connectNulls />
              <Line type="monotone" dataKey="IST" stroke="#338edc" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="text-[11px] text-[#6B7280] mt-2">{reifeFooter(periodState.year)}</div>
      </div>

      {/* ─── Segment-DB-Mini-Übersicht ──────────────────────── */}
      <SegmentMiniCard data={segmentMini} />

      {/* ─── Cockpit-Quicklinks mit Mini-Status ─────────────── */}
      <QuicklinksGrid liquidity={liquidity} audit={auditMini} report={reportMini} />

      {/* ─── Top-Abweichungen €-sortiert ────────────────────── */}
      <TopDeviationsCard alerts={alerts} />
    </div>
  );
}

/* ────────────────────────────────────────────────────────────── */
/* Sub-Components                                                 */
/* ────────────────────────────────────────────────────────────── */

function KpiCard({ kpi }) {
  const isPct = kpi.unit === 'pct';
  const fmt = isPct ? ((v) => v === null || v === undefined ? '–' : `${v.toFixed(1)} %`) : fmtEur;
  const deltaLabel = isPct ? fmtPctPoints(kpi.delta_abs) : fmtPct(kpi.delta_pct);
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
      <div className="text-[11px] text-[#6B7280] font-medium uppercase tracking-wide">{kpi.label}</div>
      <div className="mt-1 text-xl font-semibold text-[#1A1A2E] tabular-nums">{fmt(kpi.ist)}</div>
      <div className="mt-1 flex items-center justify-between text-[11px]">
        <span className="text-[#9CA3AF]">Plan {fmt(kpi.plan)}</span>
        <span className={`tabular-nums ${deltaColor(isPct ? kpi.delta_abs : kpi.delta_pct)}`}>
          {deltaLabel}
        </span>
      </div>
    </div>
  );
}

function LiquidityCard({ data }) {
  if (!data) {
    return <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 h-[100px] animate-pulse" />;
  }
  const balance = data.cockpit?.total_end ?? null;
  const delta = data.cockpit?.delta ?? null;
  return (
    <Link
      to="/investor/cashflow"
      className="bg-white border border-[#E5E7EB] rounded-xl p-4 hover:border-[#338edc] transition-colors block"
    >
      <div className="text-[11px] text-[#338edc] font-medium uppercase tracking-wide flex items-center gap-1">
        <Wallet width={11} height={11} stroke="#338edc" strokeWidth={2.5} fill="none" />
        Liquidität
      </div>
      <div className="mt-1 text-xl font-semibold text-[#1A1A2E] tabular-nums">{fmtEur(balance)}</div>
      <div className="mt-1 flex items-center justify-between text-[11px]">
        <span className="text-[#9CA3AF]">Periode</span>
        <span className={`tabular-nums ${deltaColor(delta)}`}>
          {delta !== null && delta !== undefined ? `${delta > 0 ? '+' : ''}${fmtEur(delta)}` : '–'}
        </span>
      </div>
    </Link>
  );
}

function SegmentMiniCard({ data }) {
  if (!data) {
    return <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 h-[200px] animate-pulse" />;
  }
  const segments = data.segments || [];
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-sm font-semibold text-[#1A1A2E]">Segment-DB-Übersicht</h2>
        <div className="text-[11px] text-[#6B7280]">
          Σ DB <span className="tabular-nums font-medium text-[#1A1A2E]">{fmtEur(data.sigma_segment_db)}</span>
          {' · '}
          EBITDA <span className="tabular-nums font-medium text-[#1A1A2E]">{fmtEur(data.errechnetes_ebitda)}</span>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {segments.map(s => (
          <Link
            key={s.code}
            to={SEGMENT_HREF[s.code] || '/investor'}
            className="border border-[#E5E7EB] rounded-lg p-3 hover:border-[#338edc] transition-colors group"
          >
            <div className="flex items-center justify-between">
              <div className="text-[11px] text-[#6B7280] font-medium uppercase tracking-wide truncate">{s.name}</div>
              <ArrowRight width={12} height={12} stroke="#9CA3AF" className="group-hover:stroke-[#338edc]" />
            </div>
            <div className="mt-1 text-base font-semibold text-[#1A1A2E] tabular-nums">{fmtEur(s.db)}</div>
            <div className="mt-0.5 flex items-center justify-between text-[10px]">
              <span className="text-[#9CA3AF]">Umsatz {fmtEur(s.umsatz)}</span>
              <span className={`tabular-nums ${s.db_marge_pct >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {s.db_marge_pct !== null ? `${s.db_marge_pct.toFixed(1)} %` : '–'}
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function QuicklinkTile({ to, icon: Icon, title, ampel, primary, secondary, hint }) {
  const s = AMPEL_STYLE[ampel] || AMPEL_STYLE.unknown;
  return (
    <Link
      to={to}
      className={`${s.bg} ${s.border} border rounded-xl p-4 hover:border-[#338edc] transition-colors block`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon width={14} height={14} stroke="#338edc" strokeWidth={2.5} fill="none" />
          <span className="text-[11px] text-[#338edc] font-medium uppercase tracking-wide">{title}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <AmpelDot level={ampel} />
          <span className={`text-[10px] font-medium ${s.text}`}>{s.label}</span>
        </div>
      </div>
      <div className="mt-2 text-base font-semibold text-[#1A1A2E] tabular-nums">{primary}</div>
      <div className="mt-0.5 text-[11px] text-[#6B7280]">{secondary}</div>
      {hint && <div className="mt-1 text-[10px] text-[#9CA3AF]">{hint}</div>}
    </Link>
  );
}

function QuicklinksGrid({ liquidity, audit, report }) {
  // Cashflow-Ampel: Runway > 6 grün / > 3 amber / < 3 red / "∞" grün
  const runwayMonths = liquidity?.runway?.months;
  const cashflowAmpel = runwayMonths === null && liquidity?.runway?.basis !== undefined && liquidity.runway.basis > 0
    ? 'green'
    : runwayMonths === null ? 'unknown'
    : runwayMonths < 3 ? 'red'
    : runwayMonths < 6 ? 'amber'
    : 'green';
  const cashflowPrimary = liquidity ? fmtEur(liquidity.cockpit?.total_end) : '…';
  const cashflowSecondary = liquidity
    ? (runwayMonths === null
        ? (liquidity.runway?.note || 'Runway nicht berechenbar')
        : `Runway ${runwayMonths.toFixed(1)} Monate`)
    : '';

  // Audit-Ampel: direkt aus computeAudit.master_ampel
  const auditAmpel = audit?.master_ampel || 'unknown';
  const auditPrimary = audit
    ? `${audit.offene_maengel || 0} Mängel`
    : '…';
  const auditSecondary = audit
    ? `${audit.ampel_count?.red || 0}× rot · ${audit.ampel_count?.amber || 0}× amber · ${audit.ampel_count?.green || 0}× grün`
    : '';

  // Report-Status: amber wenn pending, grün wenn aktuell vorhanden
  const reportAmpel = !report
    ? 'unknown'
    : report.next_due_pending
      ? 'amber'
      : 'green';
  const reportPrimary = report?.last_report
    ? `${MONTH_LABELS_DE[(report.last_report.monat - 1)] || ''} ${report.last_report.jahr}`
    : 'Noch kein Report';
  const reportSecondary = report?.next_due
    ? `Nächster fällig: ${MONTH_LABELS_DE[(report.next_due.monat - 1)] || ''} ${report.next_due.jahr}`
    : '';

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      <QuicklinkTile
        to="/investor/cashflow"
        icon={Wallet}
        title="Cashflow"
        ampel={cashflowAmpel}
        primary={cashflowPrimary}
        secondary={cashflowSecondary}
      />
      <QuicklinkTile
        to="/investor/szenario"
        icon={Activity}
        title="Szenario-Rechner"
        ampel="green"
        primary="Live-Hebel"
        secondary="Volumen / Preis / Effizienz"
        hint="EBITDA-Sensitivität"
      />
      <QuicklinkTile
        to="/investor/audit"
        icon={ShieldCheck}
        title="Buchhaltungs-Audit"
        ampel={auditAmpel}
        primary={auditPrimary}
        secondary={auditSecondary}
        hint={audit?.unexplained_eur !== undefined && audit?.unexplained_eur !== null
          ? `Unerklärt: ${fmtEur(audit.unexplained_eur)}`
          : null}
      />
      <QuicklinkTile
        to="/investor/report"
        icon={FileText}
        title="Investor-Report"
        ampel={reportAmpel}
        primary={reportPrimary}
        secondary={reportSecondary}
      />
    </div>
  );
}

function TopDeviationsCard({ alerts }) {
  if (!alerts || alerts.length === 0) {
    return (
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
        <h2 className="text-sm font-semibold text-[#1A1A2E] mb-3">Top-Abweichungen Plan vs IST</h2>
        <div className="h-[80px] flex items-center justify-center text-xs text-[#9CA3AF]">
          Keine Auffälligkeiten ≥ 500 €.
        </div>
      </div>
    );
  }
  const maxAbs = Math.max(...alerts.map(a => Math.abs(a.delta_abs)));
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-sm font-semibold text-[#1A1A2E]">Top-Abweichungen Plan vs IST</h2>
        <span className="text-[10px] text-[#9CA3AF] flex items-center gap-1">
          €-sortiert · % nur wenn |Plan| ≥ 1k € · ohne Aggregate
        </span>
      </div>
      <div className="space-y-1.5">
        {alerts.map((a, i) => (
          <DeviationRow key={i} alert={a} maxAbs={maxAbs} />
        ))}
      </div>
    </div>
  );
}

function DeviationRow({ alert, maxAbs }) {
  const widthPct = maxAbs > 0 ? Math.min(100, (Math.abs(alert.delta_abs) / maxAbs) * 100) : 0;
  const positive = alert.delta_abs > 0;
  const pctSuffix = alert.delta_pct_safe !== null && alert.delta_pct_safe !== undefined
    ? ` (${fmtPct(alert.delta_pct_safe)})`
    : '';
  const suppressTitle = alert.pct_suppress_reason === 'plan_too_small'
    ? '% unterdrückt: Plan < 1.000 € (Quotient wäre nicht aussagekräftig)'
    : alert.pct_suppress_reason === 'sign_flip'
      ? '% unterdrückt: Plan und IST haben unterschiedliche Vorzeichen'
      : null;
  return (
    <div className="grid grid-cols-[1fr_auto] gap-3 items-center py-1.5 border-t border-[#F1F2F4] first:border-t-0">
      <div className="min-w-0">
        <div className="flex items-center justify-between gap-2 text-[12px]">
          <span className="text-[#1A1A2E] truncate" title={labelize(alert.position)}>
            <span className="text-[10px] text-[#9CA3AF] mr-1 uppercase">{alert.bereich}</span>
            {labelize(alert.position)}
          </span>
          <span className={`tabular-nums font-medium shrink-0 ${positive ? 'text-emerald-600' : 'text-red-600'}`}>
            {positive ? '+' : ''}{fmtEur(alert.delta_abs)}{pctSuffix}
            {suppressTitle && (
              <span title={suppressTitle} className="ml-1 inline-flex items-center text-[#9CA3AF]">
                <Info width={11} height={11} stroke="#9CA3AF" strokeWidth={2.5} fill="none" />
              </span>
            )}
          </span>
        </div>
        <div className="mt-1 h-1.5 rounded-full bg-[#F1F2F4] overflow-hidden">
          <div
            className={`h-full ${positive ? 'bg-emerald-500' : 'bg-red-500'}`}
            style={{ width: `${widthPct}%` }}
          />
        </div>
        <div className="mt-1 text-[10px] text-[#9CA3AF] flex gap-3">
          <span>Plan {fmtEur(alert.plan)}</span>
          <span>IST {fmtEur(alert.ist)}</span>
        </div>
      </div>
    </div>
  );
}
